import OpenInNew from '@mui/icons-material/OpenInNew';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ApplicationSwitcher } from '@ncr-voyix-commerce/react-common-components';
import routes from '../../constants/routes';
import { useCompanyLinks } from '../../contexts/companyLinksContext';
import { isExternalLink, prependHttp, truncateString } from '../../utils';
import LinkComponent from './link-component';
import useNavItems, { NavItem } from './routes';
import styles from './style.module.css';
import { useUsers } from '../../contexts/userContext';
import { APPBAR_HEIGHT } from '../AppBar/AppBar';
import SidebarContent from './ncr-design-system-components/CustomSidebar/CustomSidebarContent';
import SidebarFooter from './ncr-design-system-components/CustomSidebar/CustomSidebarFooter';
import SidebarHeader from './ncr-design-system-components/CustomSidebar/CustomSidebarHeader';
import { NavigationBackButton } from './ncr-design-system-components/Navigation/NavigationBackButton';
import { NavigationItem } from './ncr-design-system-components/Navigation/NavigationItem';
import NavigationSwitch from './ncr-design-system-components/Navigation/NavigationSwitch';
import NavigationView from './ncr-design-system-components/Navigation/NavigationView';
import { Skeleton } from './ncr-design-system-components/Skeleton/Skeleton';
import { NavigationMenu } from './ncr-design-system-components/Navigation/NavigationMenu';
import { useSidebar } from './ncr-design-system-components/CustomSidebar/CustomSidebarContext';

function Item(props: { selectedPath: string | undefined; link: NavItem; setViewPath: (viewPath: string) => void }) {
  const { link, selectedPath, setViewPath, ...rest } = props;

  const { setCollapsed } = useSidebar();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  if (link.forceNewFrame) {
    return (
      <NavigationItem
        role='listitem'
        icon={link.icon}
        title={link.title}
        path={link.testid}
        data-testid={link.testid}
        variant='drilldown'
        onClick={() => {
          setViewPath?.(link.href || '/');
        }}
        {...rest}
      />
    );
  }
  if (link.children) {
    return (
      <NavigationItem
        selected={link.children?.some((item) => item.href === selectedPath)}
        icon={link.icon}
        title={link.title}
        path={`${link.href}*`}
        data-testid={link.testid}
        role='listitem'
      >
        <ul
          style={{
            padding: 0,
          }}
        >
          {link.children?.map((sublink) => (
            <Item selectedPath={selectedPath} key={sublink.testid} link={sublink} setViewPath={setViewPath} />
          ))}
        </ul>
      </NavigationItem>
    );
  }
  const hasInternalPath = link.href && !isExternalLink(link.href);
  return (
    <LinkComponent href={link.href} className={styles.link} role='listitem'>
      <NavigationItem
        icon={link.icon}
        title={link.title}
        path={hasInternalPath ? link.href : link.testid}
        data-testid={link.testid}
        role='none'
        tabIndex={-1}
        onClick={() => {
          if (!link.children && isMobile) setCollapsed(true);
        }}
      />
    </LinkComponent>
  );
}

interface AppSidebarProps {
  shouldHideContent?: boolean;
}

function AppSidebar(props: AppSidebarProps) {
  const { shouldHideContent } = props;
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useUsers();
  const [viewPath, setViewPath] = useState(pathname);
  useEffect(() => {
    setViewPath(pathname);
  }, [pathname]);

  const { sidebarLinks } = useCompanyLinks();
  const companyLinks = useMemo(
    () =>
      sidebarLinks?.map((link, index) => {
        const cleanedUrl = prependHttp(link.url.replaceAll(' ', ''), { https: false });
        const testid = `${link.displayName.replaceAll(' ', '')}-link-${index}`;
        return {
          title: truncateString(link.displayName, 21),
          href: cleanedUrl,
          testid,
          icon: <OpenInNew />,
          key: `${cleanedUrl}-${link.id}`,
        };
      }) || [],
    [sidebarLinks]
  );
  const links = useNavItems(companyLinks);
  const views = useMemo(() => links.filter((x) => x.forceNewFrame), [links]);

  const childrenWithForceNewFrame = links.flatMap((link) =>
    link.children ? link.children.filter((child) => child.forceNewFrame) : []
  );

  const renderSkeletons = () => {
    const rows = [];
    const widths = ['100%', '50%', '66%', '100%', '33%'];
    for (let i = 0; i < widths.length; i += 1) {
      rows.push(<Skeleton variant='rounded' height={36} sx={{ marginBottom: '15px' }} width={widths[i]} key={i} />);
    }
    return rows;
  };
  return (
    <>
      <SidebarHeader width='100%' height={APPBAR_HEIGHT} py={2}>
        {/* Do not render application switcher in preview url */}
        {process.env.REACT_APP_USE_PREVIEW_ORG_SWITCHER === 'true' ? (
          <Link to={routes.HOME_PAGE} style={{ textDecoration: 'none', color: theme.palette.text.primary }}>
            <Typography variant='h6' sx={{ width: '100%', fontWeight: 500 }}>
              {t('navItems.alohaSmartManager')}
            </Typography>
          </Link>
        ) : (
          <ApplicationSwitcher />
        )}
      </SidebarHeader>
      {!shouldHideContent && (
        <SidebarContent>
          {!user.bslAuth || user.isOrgSwitching ? (
            <Box sx={{ margin: '15px' }}>{renderSkeletons()}</Box>
          ) : (
            <NavigationSwitch view={viewPath} selection={pathname} data-testid='sidebar-navigation-switch'>
              {views.map((view) => (
                <NavigationView path={`${view.href}*`} key={view.href}>
                  <NavigationBackButton
                    title={view.title ?? ''}
                    onClick={() => {
                      setViewPath(view.backHref ?? '/');
                    }}
                  />
                  <NavigationMenu>
                    {view.children?.map((link, i) => (
                      <Item selectedPath={pathname} key={link.testid} link={link} setViewPath={setViewPath} />
                    ))}
                  </NavigationMenu>
                </NavigationView>
              ))}
              {childrenWithForceNewFrame.map((child: NavItem) => (
                <NavigationView path={`${child.href}*`} key={child.href}>
                  <NavigationBackButton
                    title={child.title ?? ''}
                    onClick={() => {
                      setViewPath(child.backHref ?? '/');
                    }}
                  />
                  <NavigationMenu>
                    {child.children?.map((grandChild) => (
                      <Item
                        selectedPath={pathname}
                        key={grandChild.testid}
                        link={grandChild}
                        setViewPath={setViewPath}
                      />
                    ))}
                  </NavigationMenu>
                </NavigationView>
              ))}
              <NavigationView path='*'>
                <NavigationMenu>
                  {links.map((link, i) => (
                    <Item selectedPath={pathname} key={link.testid} link={link} setViewPath={setViewPath} />
                  ))}
                </NavigationMenu>
              </NavigationView>
            </NavigationSwitch>
          )}
        </SidebarContent>
      )}
      <SidebarFooter sx={shouldHideContent ? { marginTop: 'auto' } : undefined}>
        <img src='/ncr-logo.png' alt='aloha-by-ncr-voyix' />
      </SidebarFooter>
    </>
  );
}

AppSidebar.defaultProps = {
  shouldHideContent: false,
};

export default AppSidebar;
